import { useEffect } from 'react';

/**
 * Hook to improve touch scrolling on mobile browsers by preventing
 * click events from firing when the user is scrolling.
 */
const useTouchScroll = (
  elementRef: React.RefObject<HTMLElement | null>,
  options: {
    threshold?: number; // Movement threshold in pixels
    debug?: boolean; // Whether to log debug info
  } = {}
) => {
  const { threshold = 10, debug = false } = options;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Only activate on mobile devices
    if (typeof window !== 'undefined' && 
        !(/iPhone|iPad|iPod|Android/i.test(navigator.userAgent))) {
      return; // Skip setup on desktop
    }
    
    let startX = 0;
    let startY = 0;
    let isScrolling = false;
    
    const handleTouchStart = (e: TouchEvent) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      isScrolling = false;
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (!e.touches[0]) return;
      
      const diffX = Math.abs(e.touches[0].clientX - startX);
      const diffY = Math.abs(e.touches[0].clientY - startY);
      
      // If moved more than threshold, mark as scrolling
      if (diffX > threshold || diffY > threshold) {
        isScrolling = true;
        if (debug) {
          console.log('Touch scroll detected:', { diffX, diffY });
        }
      }
    };
    
    const handleTouchEnd = () => {
      // Reset after some delay to prevent immediate clicks
      setTimeout(() => {
        isScrolling = false;
      }, 50);
    };
    
    // Prevent click if we were scrolling
    const handleClick = (e: MouseEvent) => {
      if (isScrolling) {
        if (debug) {
          console.log('Preventing click after scroll');
        }
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    // Use passive listeners for scroll events to improve performance
    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });
    element.addEventListener('click', handleClick, { capture: true });
    
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
      element.removeEventListener('click', handleClick, { capture: true });
    };
  }, [elementRef, threshold, debug]);
};

export default useTouchScroll; 